import { modeTone, statusTone } from '../../pages/time/timeData'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'approve'
export type ButtonSize = 'sm' | 'md'

const focusRing =
  'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'

export const btnGhostBorderless = `rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-200 dark:hover:bg-slate-700/60 ${focusRing}`

const MD_BOX = 'px-3 py-2 text-sm'
const SM_BOX = 'px-2.5 py-1 text-xs'

/** `base` is one of the shared btn* strings; swapping its box tokens (not appending) keeps one px/py/text per button. */
export function buttonClass(base: string, size: ButtonSize): string {
  if (!base.includes(MD_BOX)) throw new Error('buttonClass: base lacks the md box tokens')
  const sized = size === 'sm' ? base.replace(MD_BOX, SM_BOX) : `${base} pointer-coarse:min-h-11`
  return `inline-flex items-center justify-center gap-1.5 whitespace-nowrap ${sized}`
}

export type IconButtonVariant = 'ghost' | 'secondary' | 'danger'

const ICON_TONE: Record<IconButtonVariant, string> = {
  ghost: 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/60',
  secondary:
    'border border-border text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/60',
  danger: 'text-reject hover:bg-red-50 dark:hover:bg-red-950/40',
}

export function iconButtonClass(variant: IconButtonVariant, size: ButtonSize): string {
  const box = size === 'sm' ? 'p-1' : 'p-1.5 pointer-coarse:min-h-11 pointer-coarse:min-w-11'
  return `inline-flex shrink-0 items-center justify-center rounded-lg ${box} ${ICON_TONE[variant]} disabled:opacity-40 ${focusRing}`
}

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'muted'

/** Same colours as StatusPill's status map, so a Badge and a StatusPill with the same meaning look the same. */
export function badgeTone(tone: BadgeTone): string {
  switch (tone) {
    case 'success':
      return statusTone('Approved')
    case 'warning':
      return statusTone('Pending')
    case 'danger':
      return statusTone('Rejected')
    case 'muted':
      return statusTone('Cancelled')
    case 'info':
      return modeTone('B - Office')
    default:
      return statusTone('')
  }
}

export interface ErrorFacts {
  detail: string | null
  status: number | null
  noResponse: boolean
}

function detailOf(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail || null
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((item: { msg?: unknown }) => (typeof item?.msg === 'string' ? item.msg : String(item))).join('; ')
  }
  if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
    return (detail as { message: string }).message
  }
  return null
}

/** What an ErrorState can truthfully say about `error`; axios's own "Request failed with status code N" is dropped so the status is said once. */
export function describeError(error: unknown): ErrorFacts {
  if (error == null) return { detail: null, status: null, noResponse: false }
  if (typeof error === 'object' && (error as { isAxiosError?: unknown }).isAxiosError === true) {
    const response = (error as { response?: { status?: unknown; data?: unknown } }).response
    if (!response) return { detail: null, status: null, noResponse: true }
    const status = typeof response.status === 'number' ? response.status : null
    return { detail: detailOf(response.data), status, noResponse: false }
  }
  if (error instanceof Error) return { detail: error.message || null, status: null, noResponse: false }
  if (typeof error === 'string') return { detail: error || null, status: null, noResponse: false }
  return { detail: null, status: null, noResponse: false }
}

export type StatusHint = 'forbidden' | 'notFound' | 'serverFailed'

/** Which plain explanation fits a status when the server sent no sentence of its own. */
export function statusHint(status: number | null): StatusHint | null {
  if (status === 401 || status === 403) return 'forbidden'
  if (status === 404) return 'notFound'
  if (status !== null && status >= 500) return 'serverFailed'
  return null
}

/** A disabled control says why on hover; an enabled one keeps its own title. */
export function disabledTitle(disabled: boolean, reason: string | undefined, title: string | undefined): string | undefined {
  return disabled && reason ? reason : title
}

/** Width presets, so a page picks a role rather than a number. */
export const width = {
  prose: 'max-w-prose',
  form: 'max-w-xl',
  search: 'w-full sm:w-64',
  filter: 'w-full sm:w-48',
  colId: 'w-24',
  colDate: 'w-28',
  colStatus: 'w-32',
  colAmount: 'w-32 text-right tabular-nums',
  colName: 'min-w-48',
  colActions: 'w-px whitespace-nowrap',
} as const

/** One footprint for Loading, Error and Empty in the same place, so swapping between them does not move the page. */
export const stateRegionClass = 'flex min-h-48 flex-col justify-center'

export type NoticeTone = 'warning' | 'info' | 'danger'

export function noticeTone(tone: NoticeTone): string {
  switch (tone) {
    case 'info':
      return 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-200'
    case 'danger':
      return 'border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200'
    default:
      return 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200'
  }
}
